import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';

const SCHOOL_LOGO = 'https://customer-assets.emergentagent.com/job_89f0a926-232e-4f27-8bd9-23de150e4a0b/artifacts/ydf6w6ly_New%20St.%20Franic%20School%20Logo.jpg';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { user } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();

  // Primary navigation - exact match to image (6 items only)
  const primaryMenu = [
    { label: 'Home', path: '/', isButton: true }, // Home styled as button
    { label: 'About Us', path: '/about' },
    { label: 'Management', path: '/management' },
    { label: 'Academics', path: '/academics' },
    { label: 'Achievements', path: '/achievements' },
  ];

  // All extra items moved to "More" dropdown
  const moreMenu = {
    label: 'More',
    submenu: [
      { label: 'Admissions', path: '/admissions' },
      { label: 'Online Admission Form', path: '/admission-form' },
      { label: 'Activities', path: '/activities' },
      { label: 'Coding Lab', path: '/tekielab' },
      { label: 'Spoken English Lab', path: '/speaklab' },
      { label: 'News & Events', path: '/news' },
      { label: 'Gallery', path: '/gallery' },
      { label: 'Events Calendar', path: '/events' },
      { label: 'Resources', path: '/resources' },
      { label: 'Rules & Policies', path: '/rules' },
      { label: 'Contact Us', path: '/contact' },
      { label: 'Student Portal', path: '/student-portal' },
      { label: 'Parent Portal', path: '/parent-portal' },
      { label: 'Teacher Dashboard', path: '/teacher-dashboard' },
    ]
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-yellow-50 via-yellow-100 to-yellow-50 shadow-md border-b border-yellow-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* LEFT: Logo + School Name */}
          <Link to="/" className="flex items-center space-x-3 group flex-shrink-0">
            <img
              src={SCHOOL_LOGO}
              alt="New St. Francis School Logo"
              className="h-14 w-14 object-contain rounded-full ring-2 ring-blue-900 group-hover:ring-amber-500 transition-all duration-300"
            />
            <div>
              <h1 className="text-lg font-bold text-blue-900 leading-tight">New St. Francis School</h1>
              <p className="text-xs text-orange-600 font-bold tracking-wide">Education is Above All</p>
            </div>
          </Link>

          {/* CENTER: Primary Navigation - Desktop Only (Exact 6 items from image) */}
          <div className="hidden lg:flex items-center space-x-10">
            {primaryMenu.map((item, index) => (
              <div key={index}>
                {item.isButton ? (
                  <Link
                    to={item.path}
                    className="px-6 py-2.5 rounded-full bg-blue-900 text-white text-[15px] font-medium hover:bg-blue-800 transition-all duration-200 shadow-md"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <Link
                    to={item.path}
                    className="px-4 text-[15px] font-medium text-slate-700 hover:text-blue-900 transition-colors whitespace-nowrap"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}

            {/* More Dropdown - Exact from image */}
            <div
              className="relative group"
              onMouseEnter={() => setOpenDropdown('more')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button className="px-4 text-[15px] font-medium text-slate-700 hover:text-blue-900 transition-colors flex items-center space-x-1 whitespace-nowrap">
                <span>{moreMenu.label}</span>
                <ChevronDown className="w-4 h-4 ml-0.5" />
              </button>

              {openDropdown === 'more' && (
                <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-2xl py-2 border border-slate-100 animate-fade-in-up">
                  {moreMenu.submenu.map((subItem, subIndex) => (
                    <Link
                      key={subIndex}
                      to={subItem.path}
                      className="block px-5 py-2.5 text-[14px] text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors font-medium"
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Utility Section - Exact from image */}
          <div className="hidden lg:flex items-center space-x-5">
            {/* Language Toggle - Globe icon + EN */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1.5 text-slate-700 hover:text-blue-900 transition-colors"
              data-testid="language-toggle-button"
            >
              <Globe className="w-5 h-5" />
              <span className="text-sm font-semibold">{language === 'en' ? 'EN' : 'हि'}</span>
            </button>

            {/* Login Button - Blue with yellow text */}
            {/* {!user && (
              <Link to="/login">
                <button className="px-6 py-2.5 rounded-full bg-blue-900 text-yellow-400 text-[15px] font-bold hover:bg-blue-800 transition-all duration-200 shadow-md">
                  Login
                </button>
              </Link>
            )} */}

            {/* Apply Now (hidden in original, but keep for functionality) */}
            <Link to="/admission-form" className="hidden">
              <button className="px-6 py-2.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg">
                Apply Now
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-yellow-200 text-slate-700"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-yellow-300 max-h-[80vh] overflow-y-auto bg-yellow-50" data-testid="mobile-menu">
            {primaryMenu.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-[15px] font-medium transition-colors ${isActive(item.path) ? 'bg-blue-900 text-white' : 'text-slate-700 hover:bg-yellow-200'
                  }`}
              >
                {item.label}
              </Link>
            ))}

            {/* More Menu Items */}
            <div className="mt-2 pt-2 border-t border-yellow-300">
              <p className="px-4 py-2 text-xs font-bold text-slate-500 uppercase">More</p>
              {moreMenu.submenu.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-[15px] text-slate-700 hover:bg-yellow-200 rounded-lg font-medium"
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Mobile Actions */}
            <div className="mt-4 pt-4 border-t border-yellow-300 space-y-3 px-4">
              <button
                onClick={toggleLanguage}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg text-slate-700 hover:bg-yellow-200"
              >
                <Globe className="w-5 h-5" />
                <span className="font-medium">{language === 'en' ? 'Switch to Hindi' : 'अंग्रेजी में बदलें'}</span>
              </button>

              {!user && (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <button className="w-full px-4 py-3 rounded-full bg-blue-900 text-yellow-400 font-bold">
                    Login
                  </button>
                </Link>
              )}

              <Link to="/admission-form" onClick={() => setMobileMenuOpen(false)}>
                <button className="w-full px-4 py-3 rounded-full bg-orange-500 text-white font-bold">
                  Apply Now
                </button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}